import { camelCase, intersection } from 'lodash';
import naturalCompare from 'natural-compare-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FieldSectionType } from 'api/field-sections/types';
import { CompanyId, ProjectId, UserStatus, UserType } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { Field, FieldOption } from 'api/tasks';
import {
  FieldDataType,
  KeyEventsAndIntervalFieldTypes,
} from 'components/organisms/module-settings/fields/types';
import {
  FieldType,
  filterableFieldType,
  SourceType,
} from 'components/organisms/tasks/fields';
import { RootState } from 'data';
import {
  useDoesPartnerMatchPermissionName,
  useIsCurrentUserPartner,
} from 'hooks/use-partner';
import { useUser } from 'hooks/use-user';
import { useProjectFieldsQuery } from 'queries';
import { useProjectsFromInitData } from 'queries/account';
import { useUsersQuery } from 'queries/members';
import { constructUserMeta } from 'stores/fields/util';
import { UsersStore } from 'stores/users';
import { userOption } from './construct-user-option';

export const default_project_fields = [
  'projectName',
  'startDate',
  'dueDate',
  'customer',
  'projectOwner',
  'projectId',
  'projectFee',
  'inferredProgress',
  'currency',
  'customerChampion',
  'phaseId',
  'annualizedRecurringRevenue',
];
const feature_specific_project_fields = ['budgetedHours'];
const getSourceType = (field: Field): SourceType => {
  if (field.objectType === FieldSectionType.Project) {
    return SourceType.Project;
  }
  if (field.objectType === FieldSectionType.Users) {
    return SourceType.User;
  }

  return SourceType.Task;
};

export const nonDefaultField = (f: Field): boolean =>
  !default_project_fields.includes(camelCase(f.fieldName));

const nonFeatureField = (f: Field): boolean =>
  !feature_specific_project_fields.includes(camelCase(f.fieldName));

export const isSelectTypeField = (field: Field): boolean => {
  const type = filterableFieldType(field);

  return [
    FieldType.SingleSelect,
    FieldType.MultiSelect,
    FieldType.SingleUser,
    FieldType.MultiUser,
  ].includes(type);
};

const EMPTY_ARRAY = [];

interface FieldWithSource extends Field {
  sourceType: SourceType;
}

export const useProjectFields = () => {
  const { data } = useProjectFieldsQuery();
  const { data: projectData } = useProjectsFromInitData();

  const { data: allUsers } = useUsersQuery();
  const { user: currentUser } = useUser();
  const projectIdsFromQuery = projectData?.projects?.map((x) => x.projectId);

  const [projectIds, setProjectIds] = useState(projectIdsFromQuery);

  useEffect(() => {
    // tried using useSelector with memozing it didn't help since it recomputed the projectid.
    setProjectIds((old) => {
      if (old?.length === projectIdsFromQuery?.length) {
        return old;
      }

      return projectIdsFromQuery;
    });
  }, [projectIdsFromQuery]);
  const membershipsStore = useSelector((store: RootState) => store.memberships);
  const usersStore = useSelector((store: RootState) => store.users);
  const [loading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<FieldWithSource[] | undefined>();

  const getComputedData = useCallback(() => {
    return new Promise<FieldWithSource[]>((res) => {
      setIsLoading(true);
      setTimeout(() => {
        const dataTemp = () => {
          if (data == null || !projectIds || !allUsers) {
            return EMPTY_ARRAY;
          }

          const private_user_ids = UsersStore.ofCompanyId(
            CompanyId(currentUser.company.companyId),
          )(usersStore);
          const nativeUsers = allUsers.filter(
            (u) =>
              u.userType === UserType.Native &&
              u.userStatus !== UserStatus.Inactive,
          );
          const nonExternalUsers = allUsers.filter(
            (u) => u.userType !== UserType.External,
          );
          const non_external_user_ids = nonExternalUsers.map((x) => x.userId);
          const active_user_ids = nativeUsers.map((x) => x.userId);
          const private_active_user_ids = intersection(
            private_user_ids,
            non_external_user_ids,
          );
          const users = constructUserMeta({
            membershipsStore,
            usersStore,
            current_company_user_ids: private_user_ids,
            native_user_ids: nativeUsers.map((user) => user.userId),
            active_user_ids,
            non_external_user_ids,
            current_company_active_user_ids: private_active_user_ids,
            projectIds: projectIds.map((id) => ProjectId(id)),
            users_list: nonExternalUsers,
            currentUser,
          });
          const userOptions = (nonExternalUsers ?? []).map(
            userOption,
          ) as FieldOption<number>[];

          return data
            .filter(nonFeatureField)
            .map((field) => {
              return field.fieldDataType === FieldDataType.User
                ? {
                    ...field,
                    metaData: {
                      ...field.metaData,
                      users,
                      options: userOptions,
                    },
                    sourceType: getSourceType(field),
                  }
                : { ...field, sourceType: getSourceType(field) };
            })
            .sort((fieldA, fieldB) =>
              naturalCompare(fieldA.fieldLabel || '', fieldB.fieldLabel || ''),
            );
        };

        res(dataTemp());
      }, 0);
    });
  }, [allUsers, currentUser, data, membershipsStore, projectIds, usersStore]);

  useEffect(() => {
    (async () => {
      getComputedData().then((data2) => {
        setIsLoading(false);

        setFields(data2);
      });
    })();
  }, [getComputedData]);

  return {
    data: fields,
    isLoading: fields == null || loading,
  };
};

export const useProjectCustomFields = (
  filterFieldsBy: (f: Field) => boolean = nonDefaultField,
) => {
  const isCurrentUserPartner = useIsCurrentUserPartner();
  const isPermissivePartner = useDoesPartnerMatchPermissionName(
    PartnerPermissionName.Permissive,
  );
  const { data, isLoading } = useProjectFields();
  const fields = useMemo(() => {
    if (isCurrentUserPartner && !isPermissivePartner) {
      return [];
    }
    if (!isLoading) {
      return (data ?? []).filter(filterFieldsBy);
    }

    return [];
  }, [
    isCurrentUserPartner,
    isPermissivePartner,
    isLoading,
    data,
    filterFieldsBy,
  ]);

  return {
    data: fields,
    isLoading,
  };
};

export const useKeyEvents = (): Field[] => {
  const { data } = useProjectFieldsQuery();

  return (
    data?.filter(
      (field) =>
        field.fieldType === KeyEventsAndIntervalFieldTypes.KeyEvents &&
        field.metaData?.relatedFieldId,
    ) ?? []
  );
};

export const useAllKeyEvents = (): Field[] => {
  const { data } = useProjectFieldsQuery();

  return (
    data?.filter(
      (field) => field.fieldType === KeyEventsAndIntervalFieldTypes.KeyEvents,
    ) ?? []
  );
};

export const useIntervals = (): Field[] => {
  const { data } = useProjectFieldsQuery();

  return (
    data?.filter(
      (field) =>
        field.fieldType === KeyEventsAndIntervalFieldTypes.Intervals &&
        field.metaData?.relatedFieldId,
    ) ?? []
  );
};

export const useAllIntervals = (): Field[] => {
  const { data } = useProjectFieldsQuery();

  return (
    data?.filter(
      (field) => field.fieldType === KeyEventsAndIntervalFieldTypes.Intervals,
    ) ?? []
  );
};
